import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { DataGrid, GridColDef, GridRowId, GridRowModel } from '@mui/x-data-grid';
import Box from '@mui/material/Box/Box';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Stack from '@mui/material/Stack/Stack';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import WikiHelpIcon from '../../components/wiki/WikiHelpIcon';
import { useStore } from '../../store/store';
import { WikiEntry } from '../../services/api/docsAgent';

type WikiEntryRow = WikiEntry & { id: GridRowId };

const normalizeUrlKey = (url?: string) => {
  if (typeof url !== 'string') {
    return '';
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  const [base] = trimmed.split('#');
  return base.toLowerCase();
};

const AdminWikiEntriesTab: React.FC = observer(() => {
  const { docsStore } = useStore();
  const { wikiEntries, wikiEntriesStatus, listEntries, createEntry, updateEntry, forceUpdateEntries } = docsStore;
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [previewTooltipId, setPreviewTooltipId] = useState('');
  const [creating, setCreating] = useState(false);
  const [newEntry, setNewEntry] = useState({ tooltipId: '', url: '' });
  const [forceUpdatingIds, setForceUpdatingIds] = useState<Set<GridRowId>>(() => new Set());
  const [forceUpdatingUrls, setForceUpdatingUrls] = useState<Set<string>>(() => new Set());

  const mapToRow = useCallback((entry: WikiEntry): WikiEntryRow => ({ ...entry, id: entry.wikiEntryID }), []);

  const rows = useMemo(() => wikiEntries.map(mapToRow), [wikiEntries, mapToRow]);
  const loading = wikiEntriesStatus === 'loading' && !wikiEntries.length;

  useEffect(() => {
    const controller = new AbortController();
    listEntries({ signal: controller.signal }).catch((error) => {
      if (error) {
        enqueueSnackbar('Kunne ikke hente wiki entries', { variant: 'error' });
        console.error(error);
      }
    });
    return () => controller.abort();
  }, [enqueueSnackbar, listEntries]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) {
      return rows;
    }
    const query = search.toLowerCase();
    return rows.filter((row) => `${row.tooltipId} ${row.url}`.toLowerCase().includes(query));
  }, [rows, search]);

  const handleRefreshClick = useCallback(() => {
    setRefreshing(true);
    listEntries({ force: true })
      .catch((error) => {
        enqueueSnackbar('Kunne ikke hente wiki entries', { variant: 'error' });
        console.error(error);
      })
      .finally(() => setRefreshing(false));
  }, [enqueueSnackbar, listEntries]);

  const handleCreateEntry = useCallback(() => {
    const tooltipId = newEntry.tooltipId.trim();
    const url = newEntry.url.trim();
    if (!tooltipId) {
      enqueueSnackbar('Tooltip ID is required', { variant: 'warning' });
      return;
    }

    setCreating(true);
    createEntry({ tooltipId, url })
      .then((entry) => {
        enqueueSnackbar('Wiki entry opprettet', { variant: 'success' });
        setNewEntry({ tooltipId: '', url: '' });
        setPreviewTooltipId(entry.tooltipId);
      })
      .catch((error) => {
        enqueueSnackbar('Kunne ikke opprette wiki entry', { variant: 'error' });
        console.error(error);
      })
      .finally(() => setCreating(false));
  }, [createEntry, enqueueSnackbar, newEntry]);

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      const url = typeof newRow.url === 'string' ? newRow.url.trim() : '';
      const tooltipId = typeof newRow.tooltipId === 'string' ? newRow.tooltipId.trim() : '';
      const previousUrl = typeof oldRow.url === 'string' ? oldRow.url : '';
      const previousTooltipId = typeof oldRow.tooltipId === 'string' ? oldRow.tooltipId : '';

      if (!tooltipId) {
        enqueueSnackbar('Tooltip ID is required', { variant: 'warning' });
        return oldRow;
      }
      const id = Number(newRow.wikiEntryID ?? newRow.id);
      const hasChanges = url !== previousUrl || tooltipId !== previousTooltipId;

      if (!hasChanges) {
        return { ...newRow, url, tooltipId };
      }

      try {
        const updated = await updateEntry(id, { tooltipId, url });
        enqueueSnackbar('Wiki entry oppdatert', { variant: 'success' });
        if (previewTooltipId && Number(oldRow.wikiEntryID ?? oldRow.id) === id) {
          setPreviewTooltipId(updated.tooltipId);
        }
        return mapToRow(updated);
      } catch (error) {
        enqueueSnackbar('Kunne ikke oppdatere wiki entry', { variant: 'error' });
        throw error;
      }
    },
    [enqueueSnackbar, mapToRow, previewTooltipId, updateEntry],
  );

  const handleForceUpdate = useCallback(
    (row: WikiEntryRow) => {
      const trimmedUrl = row.url?.trim();
      if (!trimmedUrl) {
        enqueueSnackbar('Entry mangler URL', { variant: 'warning' });
        return;
      }
      const urlKey = normalizeUrlKey(row.url);
      setForceUpdatingIds((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
      if (urlKey) {
        setForceUpdatingUrls((prev) => {
          const next = new Set(prev);
          next.add(urlKey);
          return next;
        });
      }
      forceUpdateEntries({ url: trimmedUrl, tooltipIds: [row.tooltipId] })
        .then((entries) => {
          if (entries.length) {
            enqueueSnackbar('Wiki entry oppdatert', { variant: 'success' });
            const updated = entries.find((entry) => entry.tooltipId === row.tooltipId);
            if (updated && previewTooltipId === row.tooltipId) {
              setPreviewTooltipId(updated.tooltipId);
            }
          } else {
            enqueueSnackbar('Ingen oppdateringer funnet', { variant: 'info' });
          }
        })
        .catch((error) => {
          enqueueSnackbar('Kunne ikke force oppdatere wiki entry', { variant: 'error' });
          console.error(error);
        })
        .finally(() => {
          setForceUpdatingIds((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
          });
          if (urlKey) {
            setForceUpdatingUrls((prev) => {
              const next = new Set(prev);
              next.delete(urlKey);
              return next;
            });
          }
        });
    },
    [enqueueSnackbar, forceUpdateEntries, previewTooltipId],
  );

  const columns: GridColDef[] = [
    {
      field: 'preview',
      headerName: 'Preview',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => <WikiHelpIcon tooltipId={params.row.tooltipId} />,
      valueGetter: () => undefined,
    },
    {
      field: 'tooltipId',
      headerName: 'Tooltip ID',
      flex: 1,
      minWidth: 150,
      editable: true,
    },
    {
      field: 'url',
      headerName: 'URL',
      flex: 2,
      minWidth: 250,
      editable: true,
    },
    {
      field: 'lastUpdatedAt',
      headerName: 'Last Updated',
      minWidth: 160,
      valueFormatter: (params) => (params.value ? moment(params.value).format('DD.MM.YYYY HH:mm') : '-'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      minWidth: 160,
      renderCell: (params) => {
        const urlKey = normalizeUrlKey(params.row.url);
        const isUpdating = forceUpdatingIds.has(params.id) || (urlKey ? forceUpdatingUrls.has(urlKey) : false);
        return (
          <Button variant='outlined' size='small' onClick={() => handleForceUpdate(params.row as WikiEntryRow)} disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Force Update'}
          </Button>
        );
      },
      valueGetter: () => undefined,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (wikiEntriesStatus === 'error' && !rows.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography color='error'>Kunne ikke laste wiki entries. Prøv igjen.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 800, width: '1200px', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack spacing={2}>
        <Typography variant='h6'>Wiki entries</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField fullWidth label='Tooltip ID' value={newEntry.tooltipId} onChange={(e) => setNewEntry((prev) => ({ ...prev, tooltipId: e.target.value }))} />
          <TextField fullWidth label='URL' value={newEntry.url} onChange={(e) => setNewEntry((prev) => ({ ...prev, url: e.target.value }))} />
          <Button variant='contained' onClick={handleCreateEntry} disabled={creating} sx={{ width: { sm: 240 } }}>
            {creating ? 'Lagrer...' : 'Legg til'}
          </Button>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
        <div>
          <Typography variant='body2' color='text.secondary'>
            Double click a cell to edit and press enter to save.
          </Typography>
        </div>
        <Stack direction='row' spacing={2} alignItems='center'>
          <TextField label='Search...' variant='standard' value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant='outlined' onClick={handleRefreshClick} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        disableSelectionOnClick
        experimentalFeatures={{ newEditingApi: true }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.error(error);
        }}
        onRowClick={(params) => setPreviewTooltipId((params.row as WikiEntryRow).tooltipId ?? '')}
        loading={wikiEntriesStatus === 'loading' && !!wikiEntries.length}
      />
    </Box>
  );
});

export default AdminWikiEntriesTab;
